import { z } from "zod"

import {
  GoodsReceiptPalletSortField,
  GoodsReceiptSortField,
  ProductPackagingType,
  SortingOrder,
} from "@/graphql/codegen/graphql"
import { hasMaxDecimalPlaces } from "@/utils/forms"

export type GoodsReceiptSearchSchema = z.ZodObject<{
  query: z.ZodOptional<z.ZodString>
  warehouseAreaIds: z.ZodOptional<z.ZodArray<z.ZodString>>
  field: z.ZodDefault<z.ZodOptional<z.ZodNativeEnum<typeof GoodsReceiptSortField>>>
  order: z.ZodDefault<z.ZodOptional<z.ZodNativeEnum<typeof SortingOrder>>>
}>

export const createGoodsReceiptSearchSchema = (
  defaultField: GoodsReceiptSortField,
  defaultOrder: SortingOrder = SortingOrder.Asc
): GoodsReceiptSearchSchema =>
  z.object({
    query: z.string().optional(),
    warehouseAreaIds: z.array(z.string()).optional(),
    field: z.nativeEnum(GoodsReceiptSortField).optional().default(defaultField),
    order: z.nativeEnum(SortingOrder).optional().default(defaultOrder),
  })

const goodsReceiptFileFormSchema = z.object({
  file: z.instanceof(File).nullable(),
  fileDescription: z.string().optional(),
  fileType: z.string().min(1, "File type is required"),
})

export type GoodsReceiptFileFormValues = z.infer<typeof goodsReceiptFileFormSchema>

export const goodsReceiptFileArrayFormSchema = z.object({
  files: z.array(goodsReceiptFileFormSchema),
})

export type GoodsReceiptFileArrayFormValues = z.infer<typeof goodsReceiptFileArrayFormSchema>

export const goodsReceiptPalletLpnFormSchema = z.object({
  lpn: z
    .string()
    .max(20, "LPN must not exceed 20 characters") // AS400 constraint
    .nullable(),
})

export type GoodsReceiptPalletLpnFormValues = z.infer<typeof goodsReceiptPalletLpnFormSchema>

export const softReceiveFormSchema = z.object({
  estimatedPalletsCount: z
    .number()
    .positive({ message: "Estimated Pallets must be a positive number" })
    .refine((val) => val === null || val === undefined || hasMaxDecimalPlaces(val, 2), {
      message: "Estimated Pallets must have at most 2 decimal places",
    })
    .nullable()
    .optional(),
  deliveredPalletsCount: z
    .number({ required_error: "Pallet Qty Delivered is required" })
    .positive({ message: "Pallet Qty Delivered must be a positive number" })
    .refine((val) => val === null || hasMaxDecimalPlaces(val, 2), {
      message: "Pallet Qty Delivered must have at most 2 decimal places",
    })
    .nullable()
    .refine((val) => val !== null, { message: "Pallet Qty Delivered is required" }),
  spacesUsed: z.number().int().positive({ message: "Spaces Used must be a positive integer" }).nullable().optional(),
  comment: z.string().optional(),
  holdArea: z.string().min(1, "Hold Area is required"),
})

export type SoftReceiveFormValues = z.infer<typeof softReceiveFormSchema>

export const goodsReceiptPalletsSearchSchema = z.object({
  query: z.string().optional(),
  warehouseAreaIds: z.array(z.string()).optional(),
  field: z.nativeEnum(GoodsReceiptPalletSortField).optional().default(GoodsReceiptPalletSortField.UpdatedOn),
  order: z.nativeEnum(SortingOrder).optional().default(SortingOrder.Desc),
})

export type SoftReceivedLotFormSchema = z.ZodObject<{
  goodsReceiptPalletRowLotId: z.ZodNullable<z.ZodString>
  lotNumber: z.ZodEffects<z.ZodString, string, string>
  batchNumber: z.ZodString
  packageUnitQuantity: z.ZodEffects<
    z.ZodEffects<z.ZodEffects<z.ZodNullable<z.ZodNumber>, number | null, number | null>, number | null, number | null>,
    number | null,
    number | null
  >
  packagingType: z.ZodEffects<
    z.ZodNullable<z.ZodNativeEnum<typeof ProductPackagingType>>,
    ProductPackagingType | null,
    ProductPackagingType | null
  >
  unitsCount: z.ZodEffects<
    z.ZodEffects<z.ZodEffects<z.ZodNullable<z.ZodNumber>, number | null, number | null>, number | null, number | null>,
    number | null,
    number | null
  >
  quantity: z.ZodEffects<
    z.ZodEffects<z.ZodEffects<z.ZodNullable<z.ZodNumber>, number | null, number | null>, number | null, number | null>,
    number | null,
    number | null
  >
}>

export const createSoftReceivedLotFormSchema = (hasRequiredValidation = false): SoftReceivedLotFormSchema => {
  return z.object({
    goodsReceiptPalletRowLotId: z.string().nullable(),
    lotNumber: z
      .string()
      .max(20, "Lot # must not exceed 20 characters")
      .refine((val) => !hasRequiredValidation || (val !== null && val.length > 0), {
        message: "Lot # is required",
      }),
    batchNumber: z.string().max(10, "Batch # must not exceed 10 characters"),
    packageUnitQuantity: z
      .number()
      .nullable()
      .refine((val) => !hasRequiredValidation || val !== null, { message: "Package UoM is required" })
      .refine((val) => val === null || (val >= 0.01 && val <= 99999.99), {
        message: "Package UoM must be between 0.01 and 99999.99",
      })
      .refine((val) => val === null || hasMaxDecimalPlaces(val, 2), {
        message: "Package UoM must have at most 2 decimal places",
      }),
    packagingType: z
      .nativeEnum(ProductPackagingType)
      .nullable()
      .refine((val) => !hasRequiredValidation || val !== null, {
        message: "Packaging Type is required",
      }),
    unitsCount: z
      .number()
      .nullable()
      .refine((val) => !hasRequiredValidation || val !== null, { message: "Units per Lot is required" })
      .refine((val) => val === null || Number.isInteger(val), {
        message: "Units per Lot must be an integer",
      })
      .refine((val) => val === null || val >= 1, {
        message: "Units per Lot must be at least 1",
      }),
    quantity: z
      .number()
      .nullable()
      .refine((val) => !hasRequiredValidation || val !== null, { message: "Total UoM per Lot is required" })
      .refine((val) => val === null || (val >= 0.01 && val <= 9999999.99), {
        message: "Total UoM per Lot must be between 0.01 and 9999999.99",
      })
      .refine((val) => val === null || hasMaxDecimalPlaces(val, 2), {
        message: "Total UoM per Lot must have at most 2 decimal places",
      }),
  })
}

export type SoftReceivedPalletRowFormSchema = z.ZodObject<{
  goodsReceiptPalletRowId: z.ZodNullable<z.ZodString>
  palletsCount: z.ZodEffects<
    z.ZodEffects<z.ZodNullable<z.ZodNumber>, number | null, number | null>,
    number | null,
    number | null
  >
  palletUnitQuantity: z.ZodEffects<
    z.ZodEffects<z.ZodEffects<z.ZodNullable<z.ZodNumber>, number | null, number | null>, number | null, number | null>,
    number | null,
    number | null
  >
  lots: z.ZodArray<SoftReceivedLotFormSchema>
}>

export type SoftReceivedPurchaseOrderLineFormSchema = z.ZodObject<{
  purchaseOrderLineId: z.ZodString
  goodsReceiptLineId: z.ZodString
  uomId: z.ZodString
  palletRows: z.ZodArray<SoftReceivedPalletRowFormSchema>
}>

export const createSoftReceivedPalletRowFormSchema = (
  hasRequiredValidation = false
): SoftReceivedPalletRowFormSchema => {
  const lotSchema = createSoftReceivedLotFormSchema(hasRequiredValidation)

  return z.object({
    goodsReceiptPalletRowId: z.string().nullable(),
    palletsCount: z
      .number()
      .nullable()
      .refine((val) => !hasRequiredValidation || val !== null, { message: "Pallet Qty is required" })
      .refine((val) => val === null || (Number.isInteger(val) && val >= 1), {
        message: "Pallet Qty must be a positive integer",
      }),
    palletUnitQuantity: z
      .number()
      .nullable()
      .refine((val) => !hasRequiredValidation || val !== null, { message: "Pallet UoM is required" })
      .refine((val) => val === null || val > 0, { message: "Pallet UoM must be a positive number" })
      .refine((val) => val === null || hasMaxDecimalPlaces(val, 2), {
        message: "Pallet UoM must have at most 2 decimal places",
      }),
    lots: z.array(lotSchema),
  })
}

export type SoftReceivedPurchaseOrdersFormSchema = z.ZodObject<{
  goodsReceiptId: z.ZodString
  purchaseOrders: z.ZodArray<
    z.ZodObject<{
      purchaseOrderId: z.ZodNumber
      lines: z.ZodArray<SoftReceivedPurchaseOrderLineFormSchema>
    }>
  >
}>

export const createSoftReceivedPurchaseOrdersFormSchema = (
  hasRequiredValidation = false
): SoftReceivedPurchaseOrdersFormSchema => {
  const palletRowSchema = createSoftReceivedPalletRowFormSchema(hasRequiredValidation)

  const purchaseOrderLineSchema = z.object({
    purchaseOrderLineId: z.string(),
    goodsReceiptLineId: z.string(),
    uomId: z.string(),
    palletRows: z.array(palletRowSchema),
  })

  const purchaseOrderSchema = z.object({
    purchaseOrderId: z.number(),
    lines: z.array(purchaseOrderLineSchema),
  })

  return z.object({
    goodsReceiptId: z.string(),
    purchaseOrders: z.array(purchaseOrderSchema),
  })
}

export type SoftReceivedLotFormValues = z.infer<SoftReceivedLotFormSchema>
export type SoftReceivedPalletRowFormValues = z.infer<SoftReceivedPalletRowFormSchema>
export type SoftReceivedPurchaseOrderLineFormValues = z.infer<SoftReceivedPurchaseOrderLineFormSchema>
export type SoftReceivedPurchaseOrdersFormValues = z.infer<SoftReceivedPurchaseOrdersFormSchema>
